import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

// PROMPT 158: panel del restaurador BSON. Solo aparece si el backend lo tiene encendido
// (mientras esta apagado, GET /backup/restaurar/estado responde 404 y esto no dibuja nada).
// Todo lo que muestra son conteos, nombres de colecciones y codigos: nunca valores de documentos.

const RE_TAR = /^(diario|semanal|mensual)\/(\d{4}-\d{2}-\d{2})\/tours_ny_completo_(\d{4}-\d{2}-\d{2}_\d{6}Z)\.tar\.gz$/;

function detalle(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return "No se pudo completar el pedido.";
  return typeof d === "string" ? d : JSON.stringify(d);
}

export default function RestauracionBson() {
  const [info, setInfo] = useState(null);
  const [apagado, setApagado] = useState(false);
  const [elegido, setElegido] = useState(0);
  const [tag, setTag] = useState("");
  const [frase, setFrase] = useState("");
  const [manualTar, setManualTar] = useState("");
  const [manualJson, setManualJson] = useState("");
  const [manualSha, setManualSha] = useState("");
  const [manualTarB, setManualTarB] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/backup/restaurar/estado");
      setInfo(data);
      setApagado(false);
    } catch (e) {
      if (e?.response?.status === 404) setApagado(true);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const enCurso = info?.ejecucion?.estado === "en_curso";
  useEffect(() => {
    if (!enCurso) return undefined;
    const t = setInterval(cargar, 3000);
    return () => clearInterval(t);
  }, [enCurso, cargar]);

  if (apagado || !info) return null;

  const mt = RE_TAR.exec(manualTar.trim());
  const manual = mt
    ? { tar: { key: manualTar.trim() }, json: { key: manualJson.trim() || `${mt[1]}/${mt[2]}/tours_ny_para_importar_${mt[3]}.json.gz` } }
    : null;
  const cand = manual || info.candidatos?.[elegido];
  const mtB = RE_TAR.exec(manualTarB.trim());
  const compararManual = !!(mt && mtB && mtB[3] > mt[3]);
  const lanzar = async (ruta, cuerpo, ok) => {
    setEnviando(true);
    try {
      await api.post(`/backup/restaurar/${ruta}`, cuerpo);
      toast.success(ok);
      setFrase("");
      await cargar();
    } catch (e) {
      toast.error(detalle(e));
    } finally {
      setEnviando(false);
    }
  };

  const ejec = info.ejecucion || {};
  return (
    <Card className="p-6 space-y-4" data-testid="restauracion-bson">
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-slate-600 mt-0.5" />
        <div>
          <h3 className="font-semibold text-slate-900">Restaurar copia completa (avanzado)</h3>
          <p className="text-sm text-slate-500">
            Carga la copia completa a colecciones sombra, sin tocar lo vivo, y recién después las intercambia
            (se puede revertir). No borra nada. Requiere pausar a quien escribe en la base antes de intercambiar.
          </p>
        </div>
      </div>

      {info.candidatos?.length ? (
        <select
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={elegido}
          onChange={(e) => setElegido(Number(e.target.value))}
          data-testid="restauracion-corrida"
        >
          {info.candidatos.map((c, i) => (
            <option key={c.tar.key} value={i}>{c.tar.key.replace(/^.*tours_ny_completo_/, "").replace(/\.tar\.gz$/, "")} ({c.motivo})</option>
          ))}
        </select>
      ) : (
        <p className="text-sm text-slate-500">No hay respaldos buenos con la pareja tar + json de una misma corrida.</p>
      )}

      <div className="space-y-2" data-testid="restauracion-manual">
        <label className="text-xs text-slate-500">
          Origen en el bucket (opcional): pegá la clave del tar de una corrida que ya existe en R2 y la usarán Preflight y Cargar en lugar de la lista.
          El json se deduce de la misma corrida. No crea registros de respaldo en esta base.
        </label>
        <Input placeholder="diario/AAAA-MM-DD/tours_ny_completo_AAAA-MM-DD_HHMMSSZ.tar.gz" value={manualTar} onChange={(e) => setManualTar(e.target.value)} data-testid="restauracion-manual-tar" />
        <div className="flex flex-wrap gap-2">
          <Input className="max-w-[28rem]" placeholder="clave del json (opcional)" value={manualJson} onChange={(e) => setManualJson(e.target.value)} data-testid="restauracion-manual-json" />
          <Input className="max-w-[28rem]" placeholder="SHA256 esperado del tar (opcional)" value={manualSha} onChange={(e) => setManualSha(e.target.value)} data-testid="restauracion-manual-sha" />
        </div>
        {manualTar.trim() && !manual ? <p className="text-xs text-red-700" data-testid="restauracion-manual-invalida">La clave del tar no tiene el formato esperado.</p> : null}
        {manual ? <p className="text-xs text-slate-600" data-testid="restauracion-manual-activa">Se usará la pareja indicada a mano (corrida {mt[3]}).</p> : null}
        <Input placeholder="segunda captura (la mas nueva; la de arriba es la anterior): diario/AAAA-MM-DD/tours_ny_completo_AAAA-MM-DD_HHMMSSZ.tar.gz" value={manualTarB} onChange={(e) => setManualTarB(e.target.value)} data-testid="restauracion-manual-tar-b" />
        {manualTarB.trim() && !mtB ? <p className="text-xs text-red-700" data-testid="restauracion-manual-b-invalida">La clave de la segunda captura no tiene el formato esperado.</p> : null}
        {mt && mtB && !compararManual ? <p className="text-xs text-red-700" data-testid="restauracion-manual-b-orden">La segunda captura tiene que ser posterior a la primera.</p> : null}
        <Button variant="outline" disabled={!compararManual || enCurso || enviando} data-testid="restauracion-comparar-manual-btn"
          onClick={() => lanzar("comparar", { tar_a: manualTar.trim(), tar_b: manualTarB.trim() }, "Comparacion de las dos capturas indicadas a mano lanzada (solo lectura).")}>
          Comparar las dos capturas indicadas a mano
        </Button>
        <Button variant="outline" disabled={!manual || enCurso || enviando} data-testid="restauracion-validar-pareja-btn"
          onClick={() => lanzar("validar-pareja", { tar_key: manual.tar.key, json_key: manualJson.trim() || null, sha256_tar: manualSha.trim() || null }, "Validación de la pareja lanzada (solo lectura).")}>
          Validar pareja (solo lectura)
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={!cand || enCurso || enviando} data-testid="restauracion-preflight-btn"
          onClick={() => lanzar("preflight", { tar_key: cand.tar.key, json_key: cand.json.key }, "Preflight lanzado (solo lectura).")}>
          Preflight (solo lectura)
        </Button>
        <Button variant="outline" disabled={!cand || !!manual || !info.candidatos?.[elegido + 1] || enCurso || enviando} data-testid="restauracion-comparar-btn"
          onClick={() => lanzar("comparar", { tar_a: info.candidatos[elegido + 1].tar.key, tar_b: cand.tar.key }, "Comparación lanzada (solo lectura).")}>
          Comparar con la anterior
        </Button>
        <Button variant="outline" disabled={enCurso || enviando} data-testid="restauracion-archivos-btn"
          onClick={() => lanzar("verificar-archivos", {}, "Verificación de archivos lanzada (solo lectura).")}>
          Verificar archivos
        </Button>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-slate-500">Para cargar, escribí CARGAR SOMBRAS. Para probar el rename, intercambiar o revertir, indicá el tag y escribí PROBAR RENAME, INTERCAMBIAR o REVERTIR seguido del tag.</label>
        <div className="flex flex-wrap gap-2">
          <Input className="max-w-[16rem]" placeholder="tag (ej. 20261029T120000Z)" value={tag} onChange={(e) => setTag(e.target.value.trim())} data-testid="restauracion-tag" />
          <Input className="max-w-[20rem]" placeholder="frase de confirmación" value={frase} onChange={(e) => setFrase(e.target.value)} data-testid="restauracion-frase" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button disabled={!cand || enCurso || enviando || frase !== "CARGAR SOMBRAS"} data-testid="restauracion-cargar-btn"
            onClick={() => lanzar("cargar", { tar_key: cand.tar.key, json_key: cand.json.key, confirmacion: frase, tag: tag || null }, "Carga a sombras lanzada.")}>
            Cargar sombras
          </Button>
          <Button variant="outline" disabled={!tag || enCurso || enviando || frase !== `PROBAR RENAME ${tag}`} data-testid="restauracion-rename-btn"
            onClick={() => lanzar("probar-rename", { tag, confirmacion: frase }, "Prueba de rename lanzada (colección de juguete).")}>
            Probar rename
          </Button>
          <Button variant="outline" disabled={!tag || enCurso || enviando || frase !== `INTERCAMBIAR ${tag}`} data-testid="restauracion-intercambiar-btn"
            onClick={() => lanzar("intercambiar", { tag, confirmacion: frase }, "Intercambio lanzado.")}>
            Intercambiar
          </Button>
          <Button variant="outline" disabled={!tag || enCurso || enviando || frase !== `REVERTIR ${tag}`} data-testid="restauracion-revertir-btn"
            onClick={() => lanzar("revertir", { tag, confirmacion: frase }, "Reversión lanzada.")}>
            Revertir
          </Button>
        </div>
      </div>

      <div className="text-xs text-slate-600 space-y-1" data-testid="restauracion-estado">
        <div className="flex items-center gap-2">
          {enCurso ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          <span>Tarea: {ejec.tarea || "ninguna"} · {ejec.estado}</span>
        </div>
        {ejec.progreso?.fase ? <div>Fase: {ejec.progreso.fase}{ejec.progreso.coleccion ? ` (${ejec.progreso.coleccion})` : ""}</div> : null}
        {ejec.error ? <pre className="whitespace-pre-wrap rounded bg-red-50 p-2 text-red-800">{JSON.stringify(ejec.error, null, 2)}</pre> : null}
        {ejec.resultado ? <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2" data-testid="restauracion-resultado">{JSON.stringify(ejec.resultado, null, 2)}</pre> : null}
        {info.controles?.length ? (
          <ul className="list-disc pl-5">
            {info.controles.map((c, i) => <li key={`${c.tag}-${i}`}>{c.tag} · {c.fase}</li>)}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
